import { Service, ServiceFromServer } from "@/code/service";
import { reactive, Ref, ref } from "vue";
import { skapi, ENDPOINTS } from "@/main";
import { user } from "@/code/user";

export let serviceIdList: Ref<string[]> = ref([]);
export let serviceList: { [key: string]: Service | Promise<Service> } = reactive({});
export let endOfList = ref(false);
export let didTheFirstPull = false;
export let currentService: Ref<Service> = ref(null);
export let serviceUsers: { [service_id: string]: any } = {};
export let serviceRecords: { [service_id: string]: any } = {};
export let serviceAutoMails: { [service_id: string]: any } = {};
export let serviceBulkMails: { [service_id: string]: any } = {};
export let serviceLoggers: { [service_id: string]: any } = {};
export let serviceClientSecrets: { [service_id: string]: any } = {};
export let aiModelCache: { claude?: any[]; openai?: any[] } = {};
export let aiApiKeyCache: { [service_id: string]: { claude?: string; openai?: string } } = {};
export let aiChatHistoryCache: {
    [key: string]: {
        messages: { role: 'user' | 'assistant'; content: string; isError?: boolean; isPending?: boolean; isPendingOlder?: boolean }[];
        endOfList: boolean;
        startKeyHistory: string[];
    };
} = {};

// In-flight AI agent requests keyed by `${serviceId}#${platform}`.
// Kept at module scope so a pending "Thinking..." request survives page navigation:
// if the user leaves the agent view and returns, the component can re-subscribe
// to the same promise and pick up the assistant reply when it resolves.
export let pendingAgentRequests: { [key: string]: Promise<void> } = {};

// Bumped whenever an external surface (e.g. dbfile uploads firing background
// indexing requests) wants the agent chat view to refetch its history. The
// agent view watches the per-service counter and debounces a refetch so the
// pending bubbles for those background requests appear without the user
// having to manually navigate.
export let agentHistoryRefreshTrigger: { [service_id: string]: number } = reactive({});

// Module-scope record of the last `agentHistoryRefreshTrigger[serviceId]`
// value the agent view has already consumed. Persists across mount/unmount
// so a bump that occurred while the agent view was unmounted (e.g. user was
// on the dbfile tab during a batch upload) is detected on next mount and
// forces a fresh history fetch instead of restoring stale cache.
export const agentHistoryRefreshLastSeen: { [service_id: string]: number } = {};

// Table used to privately persist the last AI API key the user entered for
// each service+platform, so we can prefill the input next time without
// requiring a round-trip to the client-secret store.
const AI_API_KEY_TABLE = "ai_api_keys";
const aiApiKeyRecordIdCache: { [unique_id: string]: string } = {};

const aiApiKeyUniqueId = (userId: string, platform: "claude" | "openai") =>
    `${userId}_${platform}`;

// Persist the API key as a private record on the admin user's account.
// Upserts by unique_id; first call creates, subsequent calls update by
// record_id (resolved + cached on demand).
export const saveAiApiKeyPrivately = async (
    serviceId: string,
    platform: "claude" | "openai",
    key: string
) => {
    // console.log("[saveAiApiKeyPrivately] called", { serviceId, platform, hasKey: !!key });
    if (!serviceId || (platform !== "claude" && platform !== "openai") || !key) {
        console.warn("[saveAiApiKeyPrivately] early return: invalid args");
        return;
    }
    const userId = user?.user_id;
    if (!userId) {
        // console.warn("[saveAiApiKeyPrivately] early return: no user_id on user", user);
        return;
    }
    const unique_id = aiApiKeyUniqueId(userId, platform);
    const data = { platform, key };
    try {
        let record_id = aiApiKeyRecordIdCache[unique_id];
        if (!record_id) {
            try {
                const found: any = await skapi.getRecords({ unique_id });
                record_id = found?.list?.[0]?.record_id || "";
                if (record_id) aiApiKeyRecordIdCache[unique_id] = record_id;
            } catch (e) {
                // console.log("[saveAiApiKeyPrivately] getRecords miss", e);
            }
        }
        if (record_id) {
            // console.log("[saveAiApiKeyPrivately] updating existing", record_id);
            await skapi.postRecord(data, { record_id });
        } else {
            // console.log("[saveAiApiKeyPrivately] creating new", { unique_id });
            const created: any = await skapi.postRecord(data, {
                unique_id,
                table: { name: AI_API_KEY_TABLE, access_group: "private" },
            });
            if (created?.record_id) aiApiKeyRecordIdCache[unique_id] = created.record_id;
            // console.log("[saveAiApiKeyPrivately] created", created?.record_id);
        }
    } catch (err) {
        // console.warn("[saveAiApiKeyPrivately] failed", err);
    }
};

// Fetch the last private-record-stored API key for the given platform.
// Returns the raw key string, or "" if none is found / on error.
export const loadAiApiKeyPrivately = async (
    platform: "claude" | "openai"
): Promise<string> => {
    if (platform !== "claude" && platform !== "openai") return "";
    const userId = user?.user_id;
    if (!userId) return "";
    const unique_id = aiApiKeyUniqueId(userId, platform);
    try {
        const found: any = await skapi.getRecords({ unique_id });
        const rec = found?.list?.[0];
        if (rec?.record_id) aiApiKeyRecordIdCache[unique_id] = rec.record_id;
        return rec?.data?.key || "";
    } catch (_) {
        return "";
    }
};

export const projectNameExamples = [
    "My trip to Tokyo",
    "Korean history knowledge",
    "Tax and finance",
    "Restaurant menu and chatbot",
    "Real estate in Toulouse",
    "Travel agency project",
    "My medical records and health tracker",
    "Warehouse inventory management",
];

export const projectDescriptionExamples = [
    "Generate optimized itineraries based on my reservations.",
    "Provide insights and answer questions based on my stored knowledge.",
    "Generate reports from my financial data.",
    "List my restaurant's menu and provide a chatbot for my customers.",
    "Query my real estate listings in Toulouse.",
    "Generate tailored itineraries based on my trip experiences.",
    "Store my medical records and track my health data.",
    "Manage my warehouse inventory, generate restock alerts and reports.",
];

export const pickProjectNamePlaceholder = () =>
    projectNameExamples[Math.floor(Math.random() * projectNameExamples.length)];

export const pickProjectPlaceholders = () => {
    const idx = Math.floor(Math.random() * projectNameExamples.length);
    return {
        name: projectNameExamples[idx],
        description: projectDescriptionExamples[idx] || "",
    };
};

export let addServiceToList = (serviceListToAdd: ServiceFromServer[]) => {
    serviceListToAdd.forEach((srvcObj: ServiceFromServer) => {
        let serviceId = srvcObj.service;
        if (!serviceList[serviceId]) {
            serviceList[serviceId] = Service.load(srvcObj).then(s => {
                if (s) {
                    serviceList[serviceId] = s;
                }

                return s;
            });

            if (serviceIdList.value.indexOf(serviceId) === -1) {
                serviceIdList.value.push(serviceId);
            }
        }
    });

    let promisesToWait = Object.values(serviceList).filter(s => s instanceof Promise) as Promise<Service>[];

    return Promise.all(promisesToWait).then(() => {
        // 다 불러오고 난 후에 timestamp 순으로 정렬 체크
        serviceIdList.value.sort((a, b) => {
            let timeA =
                new Date(
                    (serviceList[a] as Service)?.service?.timestamp ||
                    0,
                ).getTime() || 0;
            let timeB =
                new Date(
                    (serviceList[b] as Service)?.service?.timestamp ||
                    0,
                ).getTime() || 0;
            return timeB - timeA;
        });
    });
}

export let pullServices = async (options?: { service_id_to_fetch?: string; service_id_to_exclude?: string[] }) => {
    let {
        admin_private_endpoint
    } = await ENDPOINTS;

    let service_id_to_fetch = options?.service_id_to_fetch || null;

    // console.log("Pulling services with options:", options);
    if (service_id_to_fetch && serviceList[service_id_to_fetch]) {
        // 이미 로드된 서비스인 경우 바로 반환
        return serviceList[service_id_to_fetch];
    }

    let requestOptions: any = {
        service: skapi.service,
        owner: skapi.owner,
        // service_id_to_fetch
        // service_id_to_exclude
    };

    if (service_id_to_fetch) {
        requestOptions.service_id = service_id_to_fetch;
    }

    if (options?.service_id_to_exclude) {
        requestOptions.service_id_to_exclude = options.service_id_to_exclude;
    }

    let fetchOptions: any = {
        limit: 25,
        fetchMore: service_id_to_fetch ? false : didTheFirstPull ? !endOfList.value : false, // 첫 pull 때는 fetchMore 무조건 false
    };

    let srvcs = await skapi.util
        .request(
            admin_private_endpoint + "pull-services",
            requestOptions,
            {
                auth: true,
                fetchOptions,
            }
        )


    if (service_id_to_fetch) {
        if (!srvcs?.list.length) {
            return;
        }
    } else {
        endOfList.value = srvcs?.endOfList;
        didTheFirstPull = true;
    }

    addServiceToList(srvcs.list);

    if (service_id_to_fetch) {
        // wait for the specific service to load
        return serviceList[service_id_to_fetch];
    }
    else {
        return serviceList;
    }
};